import { AddressVerification } from '@dill/dill-shared';

type VerifiableField = {
  uniqueId: string;
  id: string;
  fromField: string;
  value: string | number | boolean | File | any | null;
  addressVerification?: AddressVerification | null;
  groupedPosition?: number | null;
  groupStepPosition?: number | null;
};

// Mirrors DynamicStepForm's findSiblingFieldById: looks a field up by its plain id
// (e.g. 'mailing-city'), disambiguating by group position when that id is repeated
// across multiple grouped instances (e.g. cloned trade references).
const findSiblingField = <T extends VerifiableField>(
  fields: T[],
  candidateId: string,
  reference: T
): T | undefined => {
  const candidates = fields.filter((f) => f.id.replace(/-group-\d+$/, '') === candidateId);
  if (candidates.length <= 1) return candidates[0];
  return (
    candidates.find(
      (f) =>
        f.groupedPosition === reference.groupedPosition &&
        f.groupStepPosition === reference.groupStepPosition
    ) ?? candidates[0]
  );
};

const normalizeAddress = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');

// Only these two address fields get Smarty verification — other "-address" fields
// (company, business-owner, guarantor, etc.) are left untouched.
const VERIFIABLE_ADDRESS_IDS = ['shipping-address', 'mailing-address'];

/**
 * Verifies the shipping/mailing address text fields against Smarty at submission time,
 * combining each with its sibling city/state/zip fields (rather than on blur with just
 * the street text) so Smarty has enough context to confirm a real USPS delivery point.
 * Returns a new array with `value`/`addressVerification` updated for any field verified.
 */
export const verifyAddressFieldsOnSubmit = async <T extends VerifiableField>(
  fields: T[]
): Promise<T[]> => {
  const addressFields = fields.filter(
    (f): f is T =>
      f.fromField === 'TEXT' &&
      VERIFIABLE_ADDRESS_IDS.includes((f.id || '').replace(/-group-\d+$/, '')) &&
      typeof f.value === 'string' &&
      !!f.value.trim()
  );

  if (addressFields.length === 0) return fields;

  const updates = new Map<string, { value: string; addressVerification: AddressVerification }>();

  await Promise.all(
    addressFields.map(async (field) => {
      const streetValue = (field.value as string).trim();
      const idPrefix = field.id.slice(0, -'-address'.length);
      const cityField = findSiblingField(fields, `${idPrefix}-city`, field);
      const stateField = findSiblingField(fields, `${idPrefix}-state`, field);
      const zipField = findSiblingField(fields, `${idPrefix}-zip`, field);

      const city = typeof cityField?.value === 'string' ? cityField.value.trim() : '';
      const state = typeof stateField?.value === 'string' ? stateField.value.trim() : '';
      const zip = typeof zipField?.value === 'string' ? zipField.value.trim() : '';
      const hasAddressSiblings = !!cityField;

      const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');
      const fullAddress = cityStateZip ? `${streetValue}, ${cityStateZip}` : streetValue;

      // Already verified for this exact address context — skip re-querying Smarty.
      if (
        field.addressVerification?.verified &&
        field.addressVerification?.standardizedAddress &&
        normalizeAddress(field.addressVerification.standardizedAddress) ===
          normalizeAddress(fullAddress)
      ) {
        return;
      }

      try {
        // Sent as discrete fields (rather than one combined string) so the backend
        // can flag a submitted state that doesn't actually belong to the ZIP —
        // Smarty otherwise resolves the ZIP as authoritative and silently corrects it.
        const params = new URLSearchParams({ street: streetValue });
        if (city) params.set('city', city);
        if (state) params.set('state', state);
        if (zip) params.set('zipcode', zip);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/creditApplications/verifyAddress?${params.toString()}`
        );
        const json: {
          data?: { verified: boolean; standardizedAddress: string | null; message: string | null };
        } = await response.json();
        const standardizedAddress = json?.data?.standardizedAddress ?? null;
        // The backend already only reports verified=true for a full USPS DPV match
        // ("Y"), so trust it directly — Smarty's standardized form is expected to
        // differ from the submitted text (abbreviations, added ZIP+4, punctuation).
        const verified = json?.data?.verified ?? false;

        updates.set(field.uniqueId, {
          // City/state/zip live in sibling fields — only overwrite this field's own
          // value with the standardized string when there are no siblings to hold them.
          value:
            !hasAddressSiblings && verified && standardizedAddress
              ? standardizedAddress
              : streetValue,
          // Keep the USPS message even when unverified (e.g. a partial "S"/"D" match)
          // so the UI can explain what's wrong instead of just showing nothing.
          addressVerification: {
            verified,
            standardizedAddress,
            message: json?.data?.message ?? null,
          },
        });
      } catch (error) {
        console.error('Error verifying mailing address: ', error);
      }
    })
  );

  if (updates.size === 0) return fields;

  return fields.map((f) => {
    const update = updates.get(f.uniqueId);
    return update ? { ...f, ...update } : f;
  });
};
